// Tranche AH pass 1: user detail page.
//
// Single page powers both /admin/users/:id (master-scoped) and the
// tenant-scoped /users/:id route. Behavior diverges on:
//   - tenant move / role change with restricted role values
//   - back link target
// Each role/status mutation calls PATCH /users/:id (or PATCH
// /admin/users/:id for cross-tenant moves) and audit happens server-side.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AuditFeed,
  Button,
  DangerousActionButton,
  EntityDetailLayout,
  Icon,
  Spinner,
  type AuditEntry,
} from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { fetchAuditForResource } from '../lib/detailFetchers.js';
import { useAuth } from '../auth/AuthContext.js';
import { roleLabel, statusLabel } from '../lib/humanLabels.js';

interface UserDetail {
  id: string;
  email: string;
  name: string;
  role: string;
  tenant_id: string | null;
  status: 'active' | 'invited' | 'suspended';
  mfa_enrolled: boolean;
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface UserDetailPageProps {
  scope: 'admin' | 'tenant';
}

const STATUS_TONE: Record<UserDetail['status'], string> = {
  active: 'bg-secondary-container text-on-secondary-container',
  invited: 'bg-tertiary-fixed text-on-tertiary-fixed',
  suspended: 'bg-error-container text-on-error-container',
};

export function UserDetailPage({ scope }: UserDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state: auth } = useAuth();
  const isMaster = auth.status === 'authed' && auth.user.role === 'master_admin';
  const baseRoute = scope === 'admin' ? '/api/v1/admin/users' : '/api/v1/users';
  const backHref = scope === 'admin' ? '/admin/users' : '/users';

  const [user, setUser] = useState<UserDetail | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);
  // One key per open set-password dialog: a failed submit leaves the dialog up
  // with the value intact, so pressing save again is the same intent.
  const [setPasswordKey, setSetPasswordKey] = useState(() => newIdempotencyKey());
  const [setPasswordValue, setSetPasswordValue] = useState('');
  const [setPasswordError, setSetPasswordError] = useState<string | null>(null);
  const [setPasswordSubmitting, setSetPasswordSubmitting] = useState(false);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setUser(null);
    setError(null);
    Promise.all([
      apiClient.get<{ data: UserDetail }>(`${baseRoute}/${id}`, {
        skipTenantScope: scope === 'admin',
      }),
      fetchAuditForResource('user', id, 10),
    ])
      .then(([u, a]) => {
        if (cancelled) return;
        setUser(u.data);
        setAudit(a);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load user');
      });
    return () => {
      cancelled = true;
    };
  }, [id, baseRoute, scope, reloadTick]);

  // UX-013: `key` was minted inside each of these, so retrying the same
  // confirmation dialog after a failure — which keeps the dialog open with the
  // reason intact — produced a different Idempotency-Key. The dialog owns the
  // key for the lifetime of one intent and passes it in.
  async function patchUser(body: Record<string, unknown>, key: string, reason?: string) {
    if (!user) return;
    const headers = reason ? { 'x-action-reason': reason } : undefined;
    await apiClient.withIdempotencyKey(key).patch(`${baseRoute}/${user.id}`, body, {
      ...(headers ? { headers } : {}),
      skipTenantScope: scope === 'admin',
    });
    reload();
  }

  async function invalidateSessions(key: string) {
    if (!user) return;
    await apiClient.withIdempotencyKey(key).post(
      `${baseRoute}/${user.id}/sessions/invalidate`,
      {},
      {
        skipTenantScope: scope === 'admin',
      },
    );
    reload();
  }

  async function submitSetPassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!user) return;
    setSetPasswordSubmitting(true);
    setSetPasswordError(null);
    try {
      await apiClient.withIdempotencyKey(setPasswordKey).post(
        `${baseRoute}/${user.id}/password/reset`,
        {
          new_password: setPasswordValue,
        },
        {
          skipTenantScope: scope === 'admin',
        },
      );
      setSetPasswordOpen(false);
      setSetPasswordValue('');
      reload();
    } catch (err) {
      setSetPasswordError(err instanceof ApiError ? err.message : 'Failed to set password');
    } finally {
      setSetPasswordSubmitting(false);
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate(backHref)}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Icon name="arrow_back" /> Users
        </button>
        <div className="p-6 bg-error-container text-on-error-container rounded-lg" role="alert">
          {error}
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const badges = (
    <>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${STATUS_TONE[user.status]}`}
        data-testid="user-status-chip"
      >
        {statusLabel(user.status)}
      </span>
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md ${user.mfa_enrolled ? 'bg-secondary-container text-on-secondary-container' : 'bg-surface-container-high text-on-surface-variant'}`}
        data-testid="user-mfa-chip"
      >
        {user.mfa_enrolled ? 'TOTP enrolled' : 'TOTP missing'}
      </span>
    </>
  );
  const isSelf = auth.status === 'authed' && auth.user.id === user.id;

  const safeActions = (
    <>
      <Link
        to={`${backHref}?focus=${user.id}`}
        className="inline-flex items-center px-3 h-10 rounded-md bg-surface-container text-on-surface hover:bg-surface-container-high font-medium text-sm"
      >
        <Icon name="edit" className="text-sm mr-1" /> Edit
      </Link>
      <Button
        variant="secondary"
        onClick={() => {
          setSetPasswordError(null);
          setSetPasswordValue('');
          setSetPasswordKey(newIdempotencyKey());
          setSetPasswordOpen(true);
        }}
        disabled={isSelf}
        title={isSelf ? 'Use the auth surface to change your own account' : undefined}
      >
        <Icon name="lock_reset" className="text-sm" />
        <span className="ml-1">Set password</span>
      </Button>
    </>
  );

  const dangerousActions = (
    <>
      {user.status === 'active' ? (
        <DangerousActionButton
          actionLabel="Suspend…"
          title={`Suspend ${user.name}?`}
          description="Suspending invalidates all sessions and blocks future logins."
          testId="user-suspend"
          onConfirm={(reason, intentKey) => patchUser({ status: 'suspended' }, intentKey, reason)}
        />
      ) : (
        <DangerousActionButton
          actionLabel="Reactivate…"
          title={`Reactivate ${user.name}?`}
          description="Restores the user's ability to sign in. Sessions remain invalidated until they log back in."
          testId="user-reactivate"
          onConfirm={(reason, intentKey) => patchUser({ status: 'active' }, intentKey, reason)}
        />
      )}
      <DangerousActionButton
        actionLabel="Change role…"
        title={`Change role for ${user.name}`}
        description="Role changes invalidate active sessions. Provide a justification for the audit log."
        testId="user-change-role"
        onConfirm={(reason, intentKey) =>
          patchUser(
            { role: user.role === 'tenant_admin' ? 'tenant_viewer' : 'tenant_admin' },
            intentKey,
            reason,
          )
        }
      />
      {isMaster ? (
        <DangerousActionButton
          actionLabel="Move tenant…"
          title="Move user to another tenant"
          description="Only a master admin can move a user between tenants. The user is signed out and re-enrolled on the new tenant."
          testId="user-move-tenant"
          onConfirm={(reason) => {
            // TODO(api-gap): there is no /admin/users/:id/move endpoint yet;
            // the UI surfaces the action so the audit story is correct,
            // but the call is intentionally inert until the backend lands.
            // Operator-supplied reason is held in scope here for the
            // future PATCH body — capture it so eslint sees the use.
            void reason;
            void user.id;
            return Promise.resolve();
          }}
        />
      ) : null}
      <DangerousActionButton
        actionLabel="Reset TOTP…"
        title={`Reset TOTP for ${user.name}?`}
        description="Removes the user's authenticator binding. They must re-enroll on next login."
        testId="user-reset-mfa"
        onConfirm={async (reason, intentKey) => {
          // UX-013: the key used to be minted here, so retrying inside the same
          // dialog — the common path, since a failure keeps it open — sent a new
          // Idempotency-Key and the backend could not collapse the duplicate.
          const key = intentKey;
          await apiClient.withIdempotencyKey(key).post(
            `${baseRoute}/${user.id}/mfa/reset`,
            { reason },
            {
              skipTenantScope: scope === 'admin',
            },
          );
          reload();
        }}
      />
      <DangerousActionButton
        actionLabel="Invalidate sessions"
        title={`Invalidate sessions for ${user.name}?`}
        description="Kills every active session. The user is logged out immediately."
        confirmLabel="Invalidate"
        testId="user-invalidate-sessions"
        onConfirm={async (_reason, intentKey) => {
          await invalidateSessions(intentKey);
        }}
      />
    </>
  );

  return (
    <>
      <EntityDetailLayout
        title={user.name}
        subtitle={user.email}
        meta={`Role: ${roleLabel(user.role)} · Last login: ${user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'never'}`}
        badges={badges}
        backHref={backHref}
        backLabel="Users"
        safeActions={safeActions}
        dangerousActions={dangerousActions}
        audit={<AuditFeed entries={audit} label="Recent activity" />}
      >
        <section
          aria-label="Identity"
          className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
        >
          <KV k="Email" v={user.email} />
          <KV k="Role" v={roleLabel(user.role)} />
          <KV k="Tenant" v={user.tenant_id ?? '— global admin account'} />
          <KV k="Status" v={statusLabel(user.status)} />
          <KV k="TOTP enrolled" v={user.mfa_enrolled ? 'Yes' : 'No'} />
          <KV
            k="Last login"
            v={user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
          />
        </section>
      </EntityDetailLayout>
      {setPasswordOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-set-password-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
        >
          <div className="w-full max-w-[520px] bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
            <header className="flex items-center gap-3 mb-6">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-error-container text-on-error-container">
                <Icon name="lock_reset" />
              </div>
              <h2
                id="user-set-password-title"
                className="font-headline-sm text-headline-sm text-on-surface"
              >
                Set password · {user.name}
              </h2>
            </header>
            <form onSubmit={submitSetPassword} className="space-y-4" noValidate>
              <div className="space-y-2">
                <label
                  htmlFor="user-detail-set-password"
                  className="font-label-md text-label-md text-on-surface block"
                >
                  New password
                </label>
                <input
                  id="user-detail-set-password"
                  type="password"
                  required
                  minLength={8}
                  value={setPasswordValue}
                  onChange={(event) => setSetPasswordValue(event.target.value)}
                  className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none"
                  placeholder="8+ characters"
                />
              </div>
              <div
                role="status"
                className="bg-tertiary-fixed text-on-tertiary-fixed rounded-lg px-3 py-2 font-body-md text-body-md"
              >
                <Icon name="warning" className="inline-block mr-1" />
                Existing sessions for this user will be revoked.
              </div>
              {setPasswordError ? (
                <p role="alert" className="text-error font-body-md text-body-md">
                  {setPasswordError}
                </p>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setSetPasswordOpen(false)}
                  disabled={setPasswordSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={setPasswordSubmitting}>
                  Confirm
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div>
      <div className="font-label-md text-label-md uppercase text-on-surface-variant mb-1">{k}</div>
      <div className="font-body-md text-body-md text-on-surface">{v}</div>
    </div>
  );
}
